import { useCallback, useEffect, useRef, useState, type ReactNode } from 'react'
import { ChevronLeft, ChevronRight } from 'lucide-react'

interface CarouselProps {
  /** What is being paged through, for the buttons' labels - e.g. "behavior channels". */
  label: string
  children: ReactNode
}

// Scroll positions are fractional on some displays; anything within a pixel counts as "at the end".
const EDGE_TOLERANCE_PX = 1

/**
 * Shows as many items as fit and pages through the rest with arrows and
 * dots, so nothing has to be found by scrolling sideways. Items keep their
 * own widths: a page is simply one viewport's worth, and each item snaps to
 * the left edge so none is left cut in half.
 */
export function Carousel({ label, children }: CarouselProps) {
  const trackRef = useRef<HTMLDivElement>(null)
  const [position, setPosition] = useState({ page: 0, pages: 1, atStart: true, atEnd: true })

  const measure = useCallback(() => {
    const track = trackRef.current
    if (!track) {
      return
    }
    const { scrollLeft, clientWidth, scrollWidth } = track
    const maxScroll = Math.max(0, scrollWidth - clientWidth)
    const pages = clientWidth > 0 ? Math.max(1, Math.ceil(scrollWidth / clientWidth - EDGE_TOLERANCE_PX / clientWidth)) : 1
    const atEnd = scrollLeft >= maxScroll - EDGE_TOLERANCE_PX
    setPosition({
      pages,
      // The last page rarely starts on a whole multiple of the width, so being at the end is what marks it.
      page: atEnd ? pages - 1 : Math.min(pages - 1, Math.round(scrollLeft / clientWidth)),
      atStart: scrollLeft <= EDGE_TOLERANCE_PX,
      atEnd,
    })
  }, [])

  // Re-measure when the track or anything in it changes size: a window resize, or a channel added or removed.
  useEffect(() => {
    const track = trackRef.current
    if (!track) {
      return
    }
    measure()
    const observer = new ResizeObserver(measure)
    observer.observe(track)
    Array.from(track.children).forEach((child) => observer.observe(child))
    return () => observer.disconnect()
  }, [measure, children])

  function goTo(page: number): void {
    const track = trackRef.current
    if (track) {
      track.scrollTo({ left: page * track.clientWidth, behavior: 'smooth' })
    }
  }

  const arrowClass =
    'flex h-8 w-8 items-center justify-center rounded-full border border-white/25 text-white/80 transition-colors hover:border-coral hover:text-white disabled:cursor-not-allowed disabled:opacity-25 disabled:hover:border-white/25'
  const hasPages = position.pages > 1

  return (
    <div>
      <div
        ref={trackRef}
        onScroll={measure}
        className="flex snap-x snap-mandatory gap-3 overflow-x-auto scroll-smooth pb-1 [scrollbar-width:none] [&::-webkit-scrollbar]:hidden [&>*]:snap-start"
      >
        {children}
      </div>

      {hasPages && (
        <div className="mt-3 flex items-center justify-center gap-3">
          <button type="button" aria-label={`Previous ${label}`} className={arrowClass} disabled={position.atStart} onClick={() => goTo(position.page - 1)}>
            <ChevronLeft aria-hidden="true" className="h-4 w-4" />
          </button>
          <div className="flex items-center gap-1.5">
            {Array.from({ length: position.pages }, (_, page) => (
              <button
                key={page}
                type="button"
                aria-label={`Page ${page + 1} of ${position.pages}`}
                aria-current={page === position.page ? 'true' : undefined}
                onClick={() => goTo(page)}
                className={`h-2 rounded-full transition-all ${page === position.page ? 'w-5 bg-coral' : 'w-2 bg-white/30 hover:bg-white/60'}`}
              />
            ))}
          </div>
          <button type="button" aria-label={`Next ${label}`} className={arrowClass} disabled={position.atEnd} onClick={() => goTo(position.page + 1)}>
            <ChevronRight aria-hidden="true" className="h-4 w-4" />
          </button>
        </div>
      )}
    </div>
  )
}
